import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useProgressiveRender } from '../../../hooks';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { FolderManagerActionTypes, FolderManagerItemActionNames } from '../../../interfaces';
import { SlidePreviewDialog } from '../../../pages/Presentations/components/SlidePreviewDialog';
import { markAlreadyAddedSlides } from '../../../helpers';

import { FileDropContainer } from './FileDropContainer';
import { DataStatusHandler } from './DataStatusHandler';
import { DraggableDnDContainer } from './DraggableDnDContainer';
import { FolderManagerGridItem } from './FolderManagerGridItem';
import { Button, getColumnsFromZoom } from '../../../shared';
import {
  FolderManagerGridViewProps,
  GridViewData,
  GridViewProps,
  getLayoutInSelectedTheme,
  StyledGridContainer,
  StyledGridView,
  useGridViewData,
} from './folderManagerGrid.shared';
import {
  getDateRangeFromFilters,
  GROUP_BY_OPTIONS,
  SORT_BY_OPTIONS,
  SORT_DIRECTION_OPTIONS,
  DEFAULT_SLIDES_FILTERS,
} from './FolderManagerSlidesViewFilters';
import { buttonVariants } from '@clatter/ui';
import { FolderManagerItemActions } from './FolderManagerItemActions';
import { viewStateSelectors } from '../../../store';

type AssetGroupHeaderProps = {
  displayName: string;
  slideCount: number;
  isExpanded: boolean;
  selectedCount: number;
  disableSelection?: boolean;
  isSelected?: boolean;
  asset?: FolderManagerItem;
  onAction?: (data: any) => void;
  onSelect?: (e: React.MouseEvent, asset: FolderManagerItem) => void;
  onToggleExpand: () => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
};

const NoFilterResults = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  font-size: 1.5rem;
`;

const StyledHeader = styled.div<{ $isSelected?: boolean }>`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.medium} 0;
  font-size: ${({ theme }) => theme.fontSize.h5};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.gray};
  user-select: none;
  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.palette.selectedBackgroundColor : 'transparent'};
  border-radius: 4px;

  .header-content {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.small};
    flex: 1;
    cursor: pointer;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.small};
  }

  .expand-icon {
    transition: transform 0.2s ease;
    &.collapsed {
      transform: rotate(-90deg);
    }
  }

  .slide-count {
    font-size: ${({ theme }) => theme.fontSize.default};
    font-weight: 400;
    color: ${({ theme }) => theme.palette.grayLighter};
  }
`;

export const AssetGroupHeader = ({
  slideCount,
  displayName,
  isExpanded,
  selectedCount,
  disableSelection,
  isSelected,
  asset,
  onAction,
  onSelect,
  onToggleExpand,
  onSelectAll,
  onDeselectAll,
}: AssetGroupHeaderProps) => {
  const allSelected = selectedCount === slideCount && slideCount > 0;

  return (
    <StyledHeader
      $isSelected={isSelected}
      onClick={asset && onSelect ? (e) => onSelect(e, asset) : undefined}
      style={asset && onSelect ? { cursor: 'pointer' } : undefined}
    >
      <div className="header-content">
        <span
          className={`expand-icon ${isExpanded ? '' : 'collapsed'}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
        >
          ▼
        </span>
        <span>{displayName}</span>

        <span className="slide-count">({slideCount} slides)</span>

        {asset && onAction && disableSelection && (
          <span onClick={(e) => e.stopPropagation()}>
            <FolderManagerItemActions item={asset} onAction={onAction} />
          </span>
        )}
      </div>

      <div className="header-actions">
        {!disableSelection && (
          <Button
            testId="select-all-asset-slides-button"
            variant={buttonVariants.clear}
            onClick={(e: any) => {
              e.stopPropagation();
              allSelected ? onDeselectAll() : onSelectAll();
            }}
          >
            {allSelected ? `Deselect All (${selectedCount})` : `Select All (${slideCount})`}
          </Button>
        )}
      </div>
    </StyledHeader>
  );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
const GridView = ({
  items,
  overId,
  noDataMessage,
  selectedRows,
  isLoading,
  disableUpload,
  acceptedDragTypes,
  viewStateKey,
  slidesViewEnabled,
  showThemePicker,
  onDoubleClick,
  onSelect,
  onGroupHeaderSelect,
  onAction,
  onAddSlide,
  zoomValue,
  compactGrid,
  compactGridMaxColumns,
  selectedKeys,
  filters,
}: GridViewProps) => {
  const [previewState, setPreviewState] = useState<{ currentIndex: number; slides: FolderManagerItem[] } | null>(null);
  const { slug } = useParams();
  const selectedThemeId = useSelector(viewStateSelectors.getSelectedThemeId(viewStateKey || ''));
  const [autoColumnsCount, setAutoColumnsCount] = useState(4);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const deferredFilters = filters || DEFAULT_SLIDES_FILTERS;

  const gridContainerRef = useRef<HTMLDivElement>(null);

  const isFileDropDisabled = slug === 'trash' || disableUpload;

  const COMPACT_MIN_ITEM_WIDTH = 120;
  const COMPACT_ITEM_GAP = 8;

  // Use zoom-based columns if zoomValue is provided, otherwise use auto-resize
  const columnsCount = zoomValue !== undefined ? getColumnsFromZoom(zoomValue) : autoColumnsCount;

  useEffect(() => {
    // skip auto-resize if zoom is controlled externally
    if (zoomValue !== undefined) return;

    const handleResize = (entry: ResizeObserverEntry) => {
      const { contentRect } = entry;
      const containerWidth = contentRect.width;

      if (compactGrid) {
        const newCols = Math.min(compactGridMaxColumns ?? 5, Math.max(1, Math.floor((containerWidth + COMPACT_ITEM_GAP) / (COMPACT_MIN_ITEM_WIDTH + COMPACT_ITEM_GAP))));
        setAutoColumnsCount(newCols);
        return;
      }

      if (containerWidth > 1600) {
        setAutoColumnsCount(5);
      } else if (containerWidth > 1200) {
        setAutoColumnsCount(4);
      } else if (containerWidth > 600) {
        setAutoColumnsCount(3);
      } else if (containerWidth > 300) {
        setAutoColumnsCount(2);
      } else {
        setAutoColumnsCount(1);
      }
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        handleResize(entry);
      }
    });

    if (gridContainerRef.current) {
      observer.observe(gridContainerRef.current);
    }

    return () => {
      if (gridContainerRef.current) {
        observer.unobserve(gridContainerRef.current);
      }
    };
  }, [zoomValue, compactGrid, compactGridMaxColumns]);

  // scroll first selected item into view when zoom changes
  useEffect(() => {
    if (zoomValue === undefined) return;

    // wait for layout to settle after zoom change
    requestAnimationFrame(() => {
      const selected = gridContainerRef.current?.querySelector('.selected');
      if (selected) {
        selected.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [zoomValue]);

  const baseGridViewData = useGridViewData(items);

  const toggleGroupExpanded = (assetId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [assetId]: prev[assetId] === undefined ? false : !prev[assetId],
    }));
  };

  const isGroupExpanded = (assetId: string) => {
    return expandedGroups[assetId] !== false;
  };

  // apply filters/sorting/grouping when in slide repository
  const gridViewData: GridViewData = useMemo(() => {
    if (!slidesViewEnabled) {
      return baseGridViewData;
    }

    const dateRange = getDateRangeFromFilters(deferredFilters);

    let filteredAssets = [...baseGridViewData.assets];

    // apply date filter
    if (dateRange) {
      filteredAssets = filteredAssets.filter((slide) => {
        const dateStr = (slide as any).updatedAt || (slide as any).createdAt;
        if (dateStr) {
          const itemDate = new Date(dateStr).getTime();
          const afterStart = !dateRange.startDate || itemDate >= dateRange.startDate;
          const beforeEnd = !dateRange.endDate || itemDate <= dateRange.endDate;
          return afterStart && beforeEnd;
        }
        return true;
      });
    }

    // sort assets
    const isDescending = deferredFilters.sortDirection === SORT_DIRECTION_OPTIONS.NEWEST;
    filteredAssets.sort((a, b) => {
      let comparison = 0;
      if (deferredFilters.sortBy === SORT_BY_OPTIONS.NAME) {
        const nameA = (a.displayName || a.name || '').toLowerCase();
        const nameB = (b.displayName || b.name || '').toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else {
        const field = deferredFilters.sortBy === SORT_BY_OPTIONS.CREATED ? 'createdAt' : 'updatedAt';
        const dateA = (a as any)[field] ? new Date((a as any)[field]).getTime() : 0;
        const dateB = (b as any)[field] ? new Date((b as any)[field]).getTime() : 0;
        comparison = dateA - dateB;
      }
      return isDescending ? -comparison : comparison;
    });

    // group by parent asset
    if (deferredFilters.groupBy === GROUP_BY_OPTIONS.PPTX) {
      const grouped: any[] = [];
      const seenAssets = new Set<string | number>();

      for (const slide of filteredAssets) {
        const parentId = slide.itemData?.id;
        if (parentId && !seenAssets.has(parentId)) {
          seenAssets.add(parentId);
          const assetSlides = filteredAssets.filter((slide) => slide.itemData?.id === parentId);
          grouped.push({
            id: `group-${parentId}`,
            type: 'asset-group',
            asset: slide.itemData as FolderManagerItem,
            displayName: slide.itemData?.displayName || slide.itemData?.name || 'Unknown Asset',
            slideCount: assetSlides.length,
            selectable: false,
          } as any);
          grouped.push(...assetSlides);
        } else if (!parentId) {
          grouped.push(slide);
        }
      }
      filteredAssets = grouped;
    }

    return {
      folders: baseGridViewData.folders,
      workspaces: baseGridViewData.workspaces,
      assets: filteredAssets,
    };
  }, [baseGridViewData, deferredFilters, slidesViewEnabled]);

  const markedAssets = useMemo(
    () => markAlreadyAddedSlides(gridViewData.assets, selectedKeys),
    [gridViewData.assets, selectedKeys],
  );

  const selectedAssetIndex = useMemo(() => {
    if (!selectedRows?.length) return -1;
    const selectedItem = selectedRows[0];
    return markedAssets.findIndex((item: any) => {
      if (item.type === 'asset-group') return false;
      if ((selectedItem as any).instanceId && (item as any).instanceId) {
        return (item as any).instanceId === (selectedItem as any).instanceId;
      }
      return item.id === selectedItem.id && item.type === selectedItem.type;
    });
  }, [selectedRows, markedAssets]);

  const { visibleItems: visibleAssets, hasMore, sentinelRef } = useProgressiveRender(markedAssets, {
    minCount: selectedAssetIndex >= 0 ? selectedAssetIndex + 1 : undefined,
  });

  const selectableSlides = useMemo(() => {
    return markedAssets.filter((item: any) => item.type !== 'asset-group');
  }, [markedAssets]);

  const visibleItems = useMemo(() => {
    const visible = [...(gridViewData?.workspaces || []), ...(gridViewData?.folders || [])];

    for (const item of markedAssets) {
      if ((item as any).type === 'asset-group') continue;
      if (
        slidesViewEnabled &&
        deferredFilters.groupBy === GROUP_BY_OPTIONS.PPTX &&
        item.itemData?.id &&
        !isGroupExpanded(item.itemData.id)
      ) {
        continue;
      }
      visible.push(item);
    }

    return visible;
  }, [gridViewData, markedAssets, deferredFilters, slidesViewEnabled, expandedGroups]);

  const handleSelect = useCallback(
    (data: any) => onSelect({ ...data, visibleItems }),
    [onSelect, visibleItems],
  );

  const compactSlides = useMemo(
    () => gridViewData.assets.filter((item: any) => item.type !== 'asset-group') as FolderManagerItem[],
    [gridViewData.assets],
  );

  const handleAction = useCallback(
    (data: any) => {
      if (compactGrid && data?.actionType === FolderManagerActionTypes.SLIDE_PREVIEW) {
        const clickedItem = data.payload.item;
        const idx = compactSlides.findIndex(
          (slide) =>
            (clickedItem.instanceId && slide.instanceId === clickedItem.instanceId) || slide.id === clickedItem.id,
        );
        setPreviewState({ currentIndex: idx >= 0 ? idx : 0, slides: compactSlides });
        return;
      }
      onAction(data);
    },
    [compactGrid, onAction, compactSlides],
  );

  const getSlidesForAsset = (assetId: string | number) => {
    return selectableSlides.filter((slide) => slide.itemData?.id === assetId);
  };

  const isAssetSelected = (assetId: string | number) => {
    return (selectedRows || []).some((row) => row.id === assetId);
  };

  const getSelectedCountForAsset = (assetId: string | number) => {
    const assetSlides = getSlidesForAsset(assetId);
    return (selectedRows || []).filter((selected) =>
      assetSlides.some((slide) => slide.instanceId === selected.instanceId),
    ).length;
  };

  // scroll to the selected item on initial controlled selection (e.g. navigate-from-search)
  const prevSelectedLengthRef = useRef(0);
  useEffect(() => {
    const nowLength = selectedRows?.length ?? 0;
    const wasEmpty = prevSelectedLengthRef.current === 0;
    prevSelectedLengthRef.current = nowLength;

    if (!wasEmpty || nowLength === 0) {
      return;
    }

    requestAnimationFrame(() => {
      const selected = gridContainerRef.current?.querySelector('.selected');
      selected?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [selectedRows]);

  return (
    <StyledGridContainer ref={gridContainerRef}>
      <DataStatusHandler
        showNoDataMessage={noDataMessage}
        message={noDataMessage}
        isLoading={isLoading}
        showLoader={isLoading}
      />

      {gridViewData?.workspaces?.length > 0 && (
        <>
          <h4 className="type-header">Workspaces</h4>

          <StyledGridView $columnsCount={columnsCount} $compactGrid={compactGrid}>
            {(gridViewData?.workspaces || []).map((item) => (
              <FolderManagerGridItem
                key={`workspaces-${item.id}`}
                item={item}
                layoutInSelectedTheme={getLayoutInSelectedTheme(item, selectedThemeId)}
                selectedRows={selectedRows}
                isUploadDisabled={true}
                overId={overId}
                acceptedDragTypes={acceptedDragTypes}
                viewStateKey={viewStateKey}
                onSelect={handleSelect}
                onAction={onAction}
                onDoubleClick={() => onDoubleClick({ row: item })}
              />
            ))}
          </StyledGridView>
        </>
      )}

      {gridViewData?.folders?.length > 0 && (
        <>
          {gridViewData?.assets?.length > 0 ||
            (gridViewData?.workspaces?.length > 0 && <h4 className="type-header">Folders</h4>)}

          <StyledGridView $columnsCount={columnsCount} $compactGrid={compactGrid}>
            {(gridViewData?.folders || []).map((item) => (
              <FolderManagerGridItem
                key={`folders-${item.id}`}
                item={item}
                layoutInSelectedTheme={getLayoutInSelectedTheme(item, selectedThemeId)}
                selectedRows={selectedRows}
                isUploadDisabled={true}
                overId={overId}
                acceptedDragTypes={acceptedDragTypes}
                viewStateKey={viewStateKey}
                onSelect={handleSelect}
                onAction={onAction}
                onDoubleClick={() => onDoubleClick({ row: item })}
              />
            ))}
          </StyledGridView>
        </>
      )}

      {(slidesViewEnabled ? baseGridViewData.assets.length > 0 : gridViewData?.assets?.length > 0) && (
        <>
          {!slidesViewEnabled && gridViewData?.folders?.length > 0 && <h4 className="type-header">Files</h4>}

          <StyledGridView $columnsCount={columnsCount} $compactGrid={compactGrid}>
            {slidesViewEnabled && gridViewData.assets.length === 0 && (
              <NoFilterResults>No slides match the selected filters.</NoFilterResults>
            )}

            {visibleAssets.map((item: any) => {
              // render asset group header
              if (item.type === 'asset-group') {
                const assetId = item.asset?.id;
                return (
                  <div key={item.id} style={{ gridColumn: '1 / -1' }}>
                    <FileDropContainer
                      isItemContainer
                      isDisabled={isFileDropDisabled}
                      onUpload={(fileList) =>
                        onAction({
                          actionType: FolderManagerActionTypes.UPLOAD,
                          payload: { files: fileList, item: item.asset },
                        })
                      }
                    >
                      <AssetGroupHeader
                        slideCount={item.slideCount}
                        displayName={item.displayName}
                        isExpanded={isGroupExpanded(assetId)}
                        selectedCount={getSelectedCountForAsset(assetId)}
                        disableSelection
                        isSelected={assetId ? isAssetSelected(assetId) : false}
                        asset={item.asset}
                        onAction={onAction}
                        onSelect={
                          onGroupHeaderSelect && assetId ? (_e, asset) => onGroupHeaderSelect([asset]) : undefined
                        }
                        onToggleExpand={() => toggleGroupExpanded(assetId)}
                        onSelectAll={() => {}}
                        onDeselectAll={() => {}}
                      />
                    </FileDropContainer>
                  </div>
                );
              }

              // skip slides if their parent group is collapsed
              if (
                slidesViewEnabled &&
                deferredFilters.groupBy === GROUP_BY_OPTIONS.PPTX &&
                !isGroupExpanded(item.itemData?.id)
              ) {
                return null;
              }

              const isAlreadyAdded = item.selectable === false;

              return (
                <FolderManagerGridItem
                  item={item}
                  key={`${item.type}-${item.id}`}
                  layoutInSelectedTheme={getLayoutInSelectedTheme(item, selectedThemeId)}
                  isUploadDisabled={isFileDropDisabled}
                  selectedRows={selectedRows}
                  overId={overId}
                  acceptedDragTypes={acceptedDragTypes}
                  viewStateKey={viewStateKey}
                  thumbnailAssetId={(item as any)?.thumbnailAssetId}
                  compact={compactGrid}
                  alreadyAdded={isAlreadyAdded}
                  disableDnD={isAlreadyAdded}
                  onAction={handleAction}
                  onSelect={handleSelect}
                  onDoubleClick={() => onDoubleClick({ row: item })}
                />
              );
            })}
          </StyledGridView>

          {hasMore && <div ref={sentinelRef} style={{ height: 1, width: '100%' }} />}
        </>
      )}

      {previewState !== null && (
        <SlidePreviewDialog
          folderSlides={previewState.slides}
          deckName={(previewState.slides[previewState.currentIndex] as any)?.parent?.displayName ?? ''}
          currentIndex={previewState.currentIndex}
          selectedKeys={selectedKeys}
          selectedThemeId={selectedThemeId}
          onClose={() => setPreviewState(null)}
          onNavigate={(index) => setPreviewState((prev) => (prev ? { ...prev, currentIndex: index } : null))}
          onToggle={(_, checked) => {
            if (!previewState) return;
            const currentItem = previewState.slides[previewState.currentIndex];
            if (!currentItem) return;
            const actionType = checked
              ? FolderManagerItemActionNames.ADD_TO_PRESENTATION
              : FolderManagerItemActionNames.REMOVE_FROM_PRESENTATION;
            handleAction({ actionType, payload: { selectedItems: [currentItem] } });
          }}
        />
      )}
    </StyledGridContainer>
  );
};

export const FolderManagerGridView = ({
  items,
  selectedRows,
  isLoading,
  noDataMessage,
  minHeight,
  viewStateKey,
  slidesViewEnabled,
  showThemePicker,
  onDragStart,
  onDragEnd,
  onDoubleClick,
  onSelect,
  onGroupHeaderSelect,
  onAction,
  onAddSlide,
  disableUpload = false,
  disableInternalDnD = false,
  acceptedDragTypes,
  zoomValue,
  compactGrid,
  compactGridMaxColumns,
  selectedKeys,
  filters,
}: FolderManagerGridViewProps) => {
  return (
    <FileDropContainer
      isDisabled={disableUpload}
      minHeight={minHeight}
      onUpload={(fileList) =>
        onAction({
          actionType: FolderManagerActionTypes.UPLOAD,
          payload: { files: fileList, item: null },
        })
      }
    >
      {disableInternalDnD ? (
        <GridView
          items={items}
          onSelect={onSelect}
          onGroupHeaderSelect={onGroupHeaderSelect}
          selectedRows={selectedRows}
          isLoading={isLoading}
          onAction={onAction}
          onDoubleClick={onDoubleClick}
          disableUpload={disableUpload}
          acceptedDragTypes={acceptedDragTypes}
          viewStateKey={viewStateKey}
          slidesViewEnabled={slidesViewEnabled}
          showThemePicker={showThemePicker}
          onAddSlide={onAddSlide}
          noDataMessage={noDataMessage}
          zoomValue={zoomValue}
          compactGrid={compactGrid}
          compactGridMaxColumns={compactGridMaxColumns}
          selectedKeys={selectedKeys}
          filters={filters}
        />
      ) : (
        <DraggableDnDContainer onDragEnd={onDragEnd} onDragStart={onDragStart} items={items}>
          <GridView
            items={items}
            onSelect={onSelect}
            onGroupHeaderSelect={onGroupHeaderSelect}
            selectedRows={selectedRows}
            isLoading={isLoading}
            onAction={onAction}
            onDoubleClick={onDoubleClick}
            disableUpload={disableUpload}
            acceptedDragTypes={acceptedDragTypes}
            viewStateKey={viewStateKey}
            slidesViewEnabled={slidesViewEnabled}
            showThemePicker={showThemePicker}
            onAddSlide={onAddSlide}
            noDataMessage={noDataMessage}
            zoomValue={zoomValue}
            compactGrid={compactGrid}
            compactGridMaxColumns={compactGridMaxColumns}
            selectedKeys={selectedKeys}
            filters={filters}
          />
        </DraggableDnDContainer>
      )}
    </FileDropContainer>
  );
};
